import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';

import { secondaryMain } from '../../../constants/colors';
import MpmNested from './MpmNested';
import SakuraCard from '../../../common/layout/SakuraCard';
import { SubmitButton } from '../../../common/forms/buttons';
import { NumberInput } from '../fields';


const axios = require('axios').default;

const useStyles = makeStyles((theme) => ({
  card: {
    padding: '12px 24px',
  },
  cardTitle: {
    marginBottom: 12,
    fontSize: 12,
    color: secondaryMain,
  },
  error: {
    color: 'red',
    fontWeight: 'bold',
  },
  success: {
    color: 'seagreen',
    fontWeight: 'bold',
  },
}));

export default function MpmConfig(props) {
  const classes = useStyles();
  const { release, jwtToken, baseApiUri } = props;

  const getUrl = (r) => `${baseApiUri}/releases/${r.id}/config/ppm`;


  // -------------------------------- State -------------------------------- //
  // ----------------------------------------------------------------------- //
  const [config, setConfig] = React.useState();
  const [errorMessage, setErrorMessage] = React.useState();
  const [successMessage, setSuccessMessage] = React.useState();
  const [disabled, setDisabled] = React.useState(true);


  // --------------------------- Lifecycle Hooks --------------------------- //
  // ----------------------------------------------------------------------- //
  React.useEffect(() => {
    if (!release) return;

    setConfig(undefined);
    setErrorMessage(undefined);
    setSuccessMessage(undefined);

    const headers = { Authorization: jwtToken };

    return axios.get(getUrl(release), { headers })
      .then(response => {
        setConfig(response.data);
        setDisabled(false);
      })
      .catch(err => {
        console.log(err);
      })
  }, [release, jwtToken]);


  // --------------------------- Event Handlers ---------------------------- //
  // ----------------------------------------------------------------------- //
  const handleChange = (field, required) => (e) => setConfig({
    ...config, 
    [field]: e.target.value || required ? Number(e.target.value) : undefined,
  });

  const handleMpmChange = (field) => (e) => setConfig({
    ...config, 
    mpm_config: {
      ...config.mpm_config, 
      [field]: Number(e.target.value),
    }
  });

  const handleCcpmMpmChange = (field) => (e) => setConfig({
    ...config, 
    ccpm_mpm_config: {
      ...config.ccpm_mpm_config, 
      [field]: Number(e.target.value),
    }
  });

  const handleSubmit = (e) => {
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    setDisabled(true);

    const headers = { Authorization: jwtToken };

    return axios.put(getUrl(release), config, {headers})
      .then(response => {
        setConfig(response.data);
        setDisabled(false);
        setSuccessMessage('Config updated successfully.');
      })
      .catch(err => {
        setErrorMessage('Could not update configuration.');
        setDisabled(false);
      })
  };


  // ------------------------------- Render -------------------------------- //
  // ----------------------------------------------------------------------- //
  const fields = () => [
    { label: 'a Min', value: config.a_min, onChange: handleChange('a_min', true), required: true },
    { label: 'a Increment', value: config.a_incr, onChange: handleChange('a_incr', true), required: true },
    { label: 'b Min', value: config.b_min, onChange: handleChange('b_min', true), required: true },
    { label: 'b Increment', value: config.b_incr, onChange: handleChange('b_incr', true), required: true },
    { label: 'c Increment', value: config.c_incr, onChange: handleChange('c_incr', true), required: true },
    { label: 'i Max', value: config.i_max, onChange: handleChange('i_max', true), required: true },
    { label: 'j Max', value: config.j_max, onChange: handleChange('j_max', true), required: true },
    { label: 'k Max', value: config.k_max, onChange: handleChange('k_max', true), required: true },
    { label: 'CCPM Weekly Delay', value: config.ccpm_weekly_delay, onChange: handleChange('ccpm_weekly_delay', false), required: false },
  ];

  return (
    <form onSubmit={ (e) => { handleSubmit(); e.preventDefault(); } }>
      { config && (
        <SakuraCard title='PPM Configuration'>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Typography className={classes.cardTitle}>PPM Config</Typography>
              { fields().map((field) => <NumberInput disabled={disabled} {...field} />) }
            </Grid>

            <Grid item xs={4}>
              <Typography className={classes.cardTitle}>PPM-MPM Config</Typography>
              <MpmNested config={config.mpm_config} onChange={handleMpmChange} disabled={disabled} />
            </Grid>

            <Grid item xs={4}>
              <Typography className={classes.cardTitle}>CCPM-MPM Config</Typography>
              <MpmNested config={config.ccpm_mpm_config} onChange={handleCcpmMpmChange} disabled={disabled} />
            </Grid>
          </Grid>

          <br/>
          <Divider />
          <br/>

          <Typography className={classes.error} align='center'>{errorMessage}</Typography>
          <Typography className={classes.success} align='center'>{successMessage}</Typography>

          <br/>

          <Box display="flex" justifyContent="center" alignItems="center">
            <SubmitButton disabled={disabled} />
          </Box>
        </SakuraCard>
      )}
    </form>
  )
}
